import React, { useState, useEffect } from 'react';
import { getServiceRequests, createServiceRequest, uploadServiceRequestAttachment } from '../../services/api';
import { useNavigate } from 'react-router-dom';
import { PageHeader, StatusBadge, PriorityBadge } from '../../components/Shared';
import { AttachmentList } from '../../components/Attachments';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import { Plus, ScanLine, Paperclip } from 'lucide-react';
import { QrScanner } from '../../components/QrScanner';
import { toast } from 'sonner';

const CustomerRequests = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [open, setOpen] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', priority: 'medium' });
  const [pendingFile, setPendingFile] = useState(null);

  const refresh = () => getServiceRequests().then(setRequests);
  useEffect(() => { refresh(); }, []);

  const submit = async () => {
    if (!form.title) return toast.error('Title required');
    try {
      const sr = await createServiceRequest(form);
      if (pendingFile) {
        try { await uploadServiceRequestAttachment(sr.id, pendingFile); }
        catch { toast.error('Attachment upload failed'); }
      }
      await refresh();
      setOpen(false);
      setForm({ title: '', description: '', priority: 'medium' });
      setPendingFile(null);
      toast.success('Work request submitted!');
    } catch (e) { toast.error('Failed to submit'); }
  };

  const handleScan = (decoded) => {
    setScanOpen(false);
    try {
      const url = new URL(decoded);
      navigate(url.pathname + url.search);
    } catch {
      if (decoded.startsWith('/')) navigate(decoded);
      else toast.error('Unrecognised QR code');
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <PageHeader title="Work Requests" description="Submit and track your work requests."
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setScanOpen(true)}><ScanLine className="h-4 w-4 mr-2" /> Scan QR</Button>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild><Button className="bg-slate-900 hover:bg-slate-800"><Plus className="h-4 w-4 mr-2" /> New Request</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Submit Work Request</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <div><Label>What's the issue?</Label><Input className="mt-1.5" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Brief description" /></div>
                  <div><Label>Details</Label><Textarea className="mt-1.5" rows={4} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Tell us more..." /></div>
                  <div><Label>Priority</Label>
                    <Select value={form.priority} onValueChange={v => setForm({ ...form, priority: v })}>
                      <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="low">Low - When convenient</SelectItem><SelectItem value="medium">Medium - This week</SelectItem><SelectItem value="high">High - Urgent</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Photo / document (optional)</Label>
                    <div className="mt-1.5 flex items-center gap-2">
                      <input type="file" accept="image/*,application/pdf" id="sr-file" className="hidden" onChange={e => setPendingFile(e.target.files?.[0] || null)} />
                      <Button type="button" variant="outline" size="sm" onClick={() => document.getElementById('sr-file')?.click()}>
                        <Paperclip className="h-4 w-4 mr-2" /> {pendingFile ? 'Change file' : 'Attach file'}
                      </Button>
                      {pendingFile && <span className="text-xs text-slate-600 truncate">{pendingFile.name}</span>}
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                  <Button onClick={submit} className="bg-slate-900 hover:bg-slate-800">Submit</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        } />
      <QrScanner open={scanOpen} onClose={() => setScanOpen(false)} onScan={handleScan} />
      <div className="space-y-3">
        {requests.map(r => (
          <div key={r.id} className="bg-white border border-slate-200 rounded-md p-5">
            <div className="flex items-center gap-2 mb-2"><span className="text-xs font-mono text-slate-500">{r.id}</span><PriorityBadge priority={r.priority} /><StatusBadge status={r.status} /></div>
            <h3 className="font-semibold text-slate-900">{r.title}</h3>
            <p className="text-sm text-slate-600 mt-1 whitespace-pre-line">{r.description}</p>
            {r.attachments?.length > 0 && <AttachmentList items={r.attachments} compact />}
            <div className="text-xs text-slate-500 mt-2">Submitted {r.createdAt} {r.linkedWorkOrder && <>· Job: <span className="font-mono">{r.linkedWorkOrder}</span></>}</div>
          </div>
        ))}
        {requests.length === 0 && <div className="p-12 text-center text-slate-500 bg-white border border-slate-200 rounded-md">No work requests yet.</div>}
      </div>
    </div>
  );
};

export default CustomerRequests;
